import {
  compositionApi,
  CompositionMaterial,
  UpdateCompositionTreatmentsRequest,
} from '@texas/api/endpoints/compositionApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { compositionEvents } from '../events';
import { useCompositionData } from './useCompositionData';
import {
  Flex,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Modal,
  ModalCloseButton,
  ModalBody,
  FormControl,
} from '@chakra-ui/react';
import { ReactSelectOption } from '@texas/types';
import { TexasFormLabel } from '../../form/TexasFormLabel';
import { TexasSelect } from '../../form/TexasSelect';
import { LoadingOverlayV2 } from '../../LoadingOverlayV2';
import { SimpleNote } from '../../SimpleNote';
import { SubmitButton } from '../../form/SubmitButton';
import { ErrorDetails } from '../../alert/ErrorDetails';
import { useEffect } from 'react';

export function CompositionTreatmentsForm({
  loading,
  compositionMaterial,
  isOpen,
  onClose,
}: {
  loading: boolean;
  compositionMaterial: CompositionMaterial | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    reset,
  } = useForm<UpdateCompositionTreatmentsRequest>();

  const { treatments, treatmentsLoading } = useCompositionData({
    vdcSupport: true,
  });

  useEffect(() => {
    if (compositionMaterial) {
      reset({
        treatmentIds: compositionMaterial.treatments.map((t) => t.id),
      });
    }
  }, [compositionMaterial, reset]);

  const { request: submitRequest, error: submitError } = useApiRequest(
    compositionApi.updateCompositionTreatments,
  );

  const onSubmit = async (data: UpdateCompositionTreatmentsRequest) => {
    await request(submitRequest, [compositionMaterial!.id, data], (_) => {
      compositionEvents.onMaterialChanged.dispatch();
      onClose();
    });
  };

  return (
    <Modal
      size="xl"
      isCentered={true}
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex flexDir="column">{t('materials.compositionMaterial')}</Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH="90dvh" overflow="auto">
          <LoadingOverlayV2
            loaded={!loading}
            label={t('materials.loadingMaterial')}
          />
          {submitError && <ErrorDetails error={submitError} />}
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Flex flexDir="column" gap={4}>
              <SimpleNote>{t('composition.vdcDescription')}</SimpleNote>
              <FormControl>
                <TexasFormLabel>{t('configuration.treatments')}</TexasFormLabel>
                <Controller
                  name="treatmentIds"
                  control={control}
                  render={({ field }) => (
                    <TexasSelect
                      {...field}
                      isMulti={true}
                      isLoading={treatmentsLoading}
                      isClearable={true}
                      value={field.value?.map<ReactSelectOption>((x) => ({
                        label: treatments?.find((p) => p.id === x)?.name ?? '',
                        value: x,
                      }))}
                      options={
                        treatments?.map<ReactSelectOption>((x) => ({
                          value: x.id,
                          label: x.name,
                        })) ?? []
                      }
                      onChange={(e) => field.onChange(e.map((x) => x.value))}
                      closeMenuOnSelect={false}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      menuPortalTarget={document.body}
                    />
                  )}
                />
              </FormControl>
              <SubmitButton
                loading={isSubmitting}
                disabled={isSubmitting || !isValid}
              >
                {t('general.save')}
              </SubmitButton>
            </Flex>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
